/**
 * Docs FAQ — one data source for the visible question/answer markup and the
 * `FAQPage` structured data (framing R3, task D.4). `answer` is plain text
 * and must say the same thing as the rendered answer.
 */

import type { FaqItem } from '../../lib/json-ld';

export const DOCS_FAQ: readonly FaqItem[] = [
  {
    id: 'faq-offset',
    question: 'What is an offset and how does it work?',
    answer:
      'The offset is the time in seconds between the corridor reference point and the start of an intersection\'s signal cycle. The first intersection usually has offset 0. Adjusting offsets shifts when each intersection\'s green begins; the goal is to align the greens so that vehicles travelling at the design speed arrive at each intersection as its green starts. Example: if intersection 2 is 400 m from intersection 1 and the design speed is 60 km/h, travel time is 400 ÷ (60 × 1000 ÷ 3600) = 24 seconds, so an offset of 24 seconds at intersection 2 gives perfect northbound progression.',
  },
  {
    id: 'faq-green-wave',
    question: 'How do I create a green wave?',
    answer:
      'Set the first intersection\'s offset to 0. For each following intersection, calculate the travel time from the previous one as distance in metres divided by speed in metres per second (km/h × 1000 ÷ 3600), and set its offset to the cumulative travel time. Then fine-tune with the offset slider while watching the progression band. Auto-sequence does this calculation for northbound or southbound progression in one click.',
  },
  {
    id: 'faq-bidirectional',
    question: 'Can I optimise for both directions?',
    answer:
      'Yes, with trade-offs. Perfect green waves in both directions at once are only possible when speeds are equal in both directions, intersection spacing is a suitable fraction of the cycle length (ideally half the distance travelled in one cycle), and the phase arrangements are symmetric. In practice engineers prioritise the higher-volume direction or accept a compromise band in each direction.',
  },
  {
    id: 'faq-phases',
    question: 'How many phases can I have?',
    answer:
      'Up to six phases per intersection, labelled A to F, and as few as two. Typical arrangements are two phases for a simple through-and-cross intersection, four with protected right or left turns, and five or six for intersections with several turn phases.',
  },
  {
    id: 'faq-cycle-length',
    question: 'How is cycle length calculated?',
    answer:
      'Cycle length is the sum of all phase durations at an intersection: Phase A + Phase B + Phase C and so on. For coordination, every intersection in the corridor should run the same cycle length, or an integer multiple of a common cycle.',
  },
  {
    id: 'faq-data-loss',
    question: 'What happens if I close the browser?',
    answer:
      'Work in progress is saved automatically to the browser\'s local storage and restored on return. Local storage is specific to that browser and device, is cleared with browser data, and may not persist in private windows, so export your plan or save it to an account to keep it.',
  },
  {
    id: 'faq-time-space',
    question: 'Is a time-distance diagram the same as a time-space diagram?',
    answer:
      'Yes. Time-distance diagram is the usual term in Australia and the United Kingdom, time-space diagram in North America. Both name the same drawing: distance along the corridor on one axis, time on the other, phase bars at each signal and sloped vehicle trajectories between them.',
  },
];
